// Angular Files
import { Injectable } from '@angular/core';

// Other External Files
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Teller Online Files
import {
    AdminScheduledPaymentsApiClient,
    AdminScheduledPaymentDetailDto,
    AdminScheduledPaymentInstanceDto,
    AdminScheduledPaymentOverviewDto,
    CancelScheduledPaymentRequestDto,
    PaymentsSearchOptionsEnumDto
} from 'apps/admin-portal/src/app/core/api/AdminPortalApiClients';

// Teller Online Library Files
import {
    ScheduledItemDetail,
    ScheduleDetailsViewModel
} from 'teller-online-libraries/shared';
import {
    SearchFilterGroupEnum,
    SearchFilterModel,
    SearchSettingsModel,
    TellerOnlineDataSourceCompatible,
    TellerOnlineFilteredResponseModel
} from 'teller-online-libraries/core';

// Component Files
import { PaymentNextPrevModel } from './admin-payments.service';

@Injectable({
    providedIn: 'root'
})
export class AdminScheduledPaymentsService
    implements TellerOnlineDataSourceCompatible<
        ScheduledPaymentOverviewModel,
        TellerOnlineFilteredScheduledPaymentsResponseModel> {
    constructor(
        private apiClient: AdminScheduledPaymentsApiClient
    ) { }

    public loadDataSource(
        settings: SearchSettingsModel
    ): Observable<TellerOnlineFilteredScheduledPaymentsResponseModel> {
        return this.apiClient
            .getFilteredScheduledPayments(settings.toDto())
            .pipe(map(response => new TellerOnlineFilteredScheduledPaymentsResponseModel({
                    results: response.results.map(
                        sp => new ScheduledPaymentOverviewModel(sp)),
                    totalResults: response.totalResults
                })));
    }

    public cancelScheduledPayment(
        guid: string,
        cancellationReason?: string
    ): Promise<void> {
        return firstValueFrom(
            this.apiClient.cancelScheduledPayment(
                guid,
                new CancelScheduledPaymentRequestDto(
                    { cancellationReason })));
    }

    public getCancellationReasons(
        guid: string
    ): Promise<string[]> {
        return firstValueFrom(
            this.apiClient
                .getScheduledPaymentCancellationReasons(
                    guid));
    }

    public getScheduledPaymentDetail(
        guid: string
    ): Promise<ScheduledPaymentDetailModel> {
        return firstValueFrom(
            this.apiClient.getScheduledPaymentDetail(guid)
                .pipe(map(
                    dto => new ScheduledPaymentDetailModel(dto))));
    }

    // Maps a schedule detail onto the shared teller-online-schedule-details
    // view model: executed instances show actuals, unexecuted ones estimates;
    // each instance links to its payment (finalized) or upcoming-instance page.
    public buildScheduleDetailsViewModel(
        detail: ScheduledPaymentDetailModel
    ): ScheduleDetailsViewModel {
        // Some endpoints return instances ordered by status rather than
        // sequence; the table (and the adjusted-final-payment detection
        // below) needs chronological order.
        const instances = [...(detail.instances || [])]
            .sort((a, b) => a.instanceNumber - b.instanceNumber);

        const rows = instances.map(i => {
            const amount = i.actualAmount ?? i.estimatedAmount;
            const fee = i.actualConvenienceFee
                ?? i.estimatedConvenienceFee
                ?? 0;

            let link: string | undefined;

            if (i.guid) {
                link = i.paymentIdentifier
                    ? `/payments/${i.paymentIdentifier}`
                    : `/payments/scheduled/${i.guid}`;
            }

            return {
                id: i.instanceNumber,
                status: i.status,
                amount,
                fee,
                total: amount + fee,
                date: i.scheduledForDate,
                link,
                clickable: !!link
            };
        });

        const hasAdjustedFinal = rows.length >= 2
            && rows[0].amount !== rows[rows.length - 1].amount;

        const payments = rows.map((row, index) => ({
            ...row,
            amountMarker: hasAdjustedFinal
                && index === rows.length - 1
                    ? '**'
                    : undefined,
        }));

        const totalFees = payments.reduce(
            (sum, p) => sum + p.fee, 0
        );

        const feesAreEstimates = instances.some(
            i => i.actualConvenienceFee == null
                && i.status !== 'Canceled'
        );

        const totalAmountPaid = instances
            .filter(i => i.status === 'CompletedSuccessfully')
            .reduce((sum, i) =>
                sum + (i.actualAmount ?? i.estimatedAmount), 0);

        return {
            payments,
            totalScheduledAmount: detail.totalPaymentAmount,
            originalBalanceDue: detail.totalPaymentAmount,
            totalAmountPaid,
            balanceRemaining:
                detail.totalPaymentAmount - totalAmountPaid,
            showFees: true,
            feesAreEstimates,
            totalFees,
            grandTotal: detail.totalPaymentAmount + totalFees,
        };
    }

    public getScheduledPaymentByInstanceGuid(
        instanceGuid: string
    ): Promise<ScheduledPaymentDetailModel> {
        return firstValueFrom(
            this.apiClient
                .getScheduledPaymentByInstanceGuid(instanceGuid)
                .pipe(map(
                    dto => new ScheduledPaymentDetailModel(dto))));
    }

    public getNextPrevByInstanceGuid(
        instanceGuid: string,
        settings: SearchSettingsModel
    ): Promise<PaymentNextPrevModel> {
        return firstValueFrom(
            this.apiClient.getNextPrevByInstanceGuid(
                instanceGuid, settings.toDto()));
    }

    public getScheduledPaymentsCount(): Promise<number> {
        const settings = this._createActiveSchedulesSettings();

        return firstValueFrom(
            this.apiClient
                .getFilteredScheduledPayments(settings.toDto())
                .pipe(map(r => r.totalResults)));
    }

    public getRecurringPaymentsCount(): Promise<number> {
        const settings = this._createActiveSchedulesSettings();

        settings.addFilter({
            label: 'No End Date',
            filters: [
                new SearchFilterModel({
                    type: PaymentsSearchOptionsEnumDto
                        .HasNoEndDate,
                    value: 'true'
                })
            ],
            filterGroup: SearchFilterGroupEnum.PaymentDate
        });

        return firstValueFrom(
            this.apiClient
                .getFilteredScheduledPayments(settings.toDto())
                .pipe(map(r => r.totalResults)));
    }

    private _createActiveSchedulesSettings():
        SearchSettingsModel {
        const settings = new SearchSettingsModel();

        settings.addFilter({
            label: 'Active Schedules',
            filters: [
                new SearchFilterModel({
                    type: PaymentsSearchOptionsEnumDto
                        .OneOfStatus,
                    value: JSON.stringify(
                        ['Pending', 'Active'])
                })
            ],
            filterGroup: SearchFilterGroupEnum.StatusGroup
        });

        settings.pageSize = 0;

        return settings;
    }
}

export class ScheduledPaymentOverviewModel {
    guid: string;

    title: string;

    status: string;

    totalPaymentAmount: number;

    startDate: string;

    endDate: string;

    interval: string;

    userName: string;

    email: string;

    nextInstanceDate: string;

    createdDate: Date;

    constructor(dto?: AdminScheduledPaymentOverviewDto) {
        if (dto) {
            this.guid = dto.guid;
            this.title = dto.title;
            this.status = dto.status;
            this.totalPaymentAmount = dto.totalPaymentAmount;
            this.startDate = dto.startDate;
            this.endDate = dto.endDate;
            this.interval = dto.interval;
            this.userName = dto.userName;
            this.email = dto.email;
            this.nextInstanceDate = dto.nextInstanceDate;
            this.createdDate = dto.createdDate;
        }
    }
}

export class ScheduledPaymentDetailModel
    extends ScheduledPaymentOverviewModel {
    businessSystemItemKey: string;

    pageName: string;

    reason: string;

    onlineUserId: number;

    paymentMethod: string;

    details: ScheduledItemDetail[];

    instances: ScheduledPaymentInstanceModel[];

    constructor(dto?: AdminScheduledPaymentDetailDto) {
        super(dto);

        if (dto) {
            this.businessSystemItemKey = dto.businessSystemItemKey;
            this.pageName = dto.pageName;
            this.reason = dto.reason;
            this.onlineUserId = dto.onlineUserId;
            this.paymentMethod = dto.paymentMethod;
            this.details = (dto.details || []).map(
                d => ({
                    description: d.label,
                    value: d.value
                }));
            this.instances = (dto.instances || []).map(
                i => new ScheduledPaymentInstanceModel(i));
        }
    }
}

export class ScheduledPaymentInstanceModel {
    guid: string;

    instanceNumber: number;

    scheduledForDate: string;

    estimatedAmount: number;

    estimatedConvenienceFee: number;

    status: string;

    actualAmount: number;

    actualConvenienceFee: number;

    paymentIdentifier: string;

    message: string;

    constructor(dto?: AdminScheduledPaymentInstanceDto) {
        if (dto) {
            this.guid = dto.guid;
            this.instanceNumber = dto.instanceNumber;
            this.scheduledForDate = dto.scheduledForDate;
            this.estimatedAmount = dto.estimatedAmount;
            this.estimatedConvenienceFee = dto.estimatedConvenienceFee;
            this.status = dto.status;
            this.actualAmount = dto.actualAmount;
            this.actualConvenienceFee = dto.actualConvenienceFee;
            this.paymentIdentifier = dto.paymentIdentifier;
            this.message = dto.message;
        }
    }
}

export class TellerOnlineFilteredScheduledPaymentsResponseModel
    extends TellerOnlineFilteredResponseModel<
        ScheduledPaymentOverviewModel> { }
